import React, { memo, useId } from 'react'

import { Icon, ChevronRight } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, array } from 'prop-types'

import { useSubcategoryMenu } from 'Pods/UnifiedNavigation/Shared/AssetItem/Hooks/useSubcategoryMenu'
import useListingCategoryNavigation from 'Pods/UnifiedNavigation/Shared/Hooks/useListingCategoryNavigation'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import MegaRow from '../MegaRow/MegaRow'
import SubcategoryFlyout from '../SubcategoryFlyout/SubcategoryFlyout'

const DISCLOSURE_CHEVRON_SIZE = { width: '12', height: '12' }

// A single category row plus its subcategory flyout. Owns one useSubcategoryMenu instance
// so each category opens independently. The row is always a navigable link (to the category
// page, or an in-place listing-filter switch via useListingCategoryNavigation when already on
// a listing) on every breakpoint. A real disclosure <button> (aria-expanded/aria-controls)
// sits beside it and reveals the flyout: hover/focus opens it on desktop, a tap toggles it at
// <=959px - so tapping the label navigates to the category while tapping the arrow drills into
// its subcategories, mirroring the legacy nav. It is keyboard-operable on both. Categories
// without subcategories render as a plain link with no disclosure.
function CategoryItem({
  title,
  iconSlug = '',
  href = undefined,
  subcategories = EMPTY_ARRAY,
  browseAllHref = undefined,
  browseAllLabel = undefined,
}) {
  const {
    isSubmenuOpen,
    hoverZoneRef,
    handleToggleSubmenu,
    handleOpenSubmenu,
    handleCloseSubmenu,
    handleHoverZoneMouseEnter,
    handleHoverZoneMouseLeave,
    handleSubmenuFocus,
    handleSubmenuBlur,
  } = useSubcategoryMenu()

  const breakpoint = useBreakpoint()
  const isMobile = ['xs', 'sm'].includes(breakpoint)
  const handleCategoryLinkClick = useListingCategoryNavigation({ isMobile })

  const generatedId = useId()
  const labelId = `${generatedId}-label`
  const flyoutId = `${generatedId}-flyout`
  const hasSubcategories = subcategories.length > 0

  // The row always navigates via the listing-category bridge (category page, or an in-place
  // listing-filter switch when already on a listing) on every breakpoint. At <=959px the arrow
  // disclosure beside it opens the flyout instead, so the label and the arrow stay separate
  // tap targets - the legacy behaviour.
  const handleRowClick = (event) => {
    handleCategoryLinkClick(event, href)
  }

  // Mobile: the disclosure toggles the flyout (tap to drill in, tap again to collapse).
  // Desktop: hover and focus already open it, and the click fires right after the focus that
  // opened it - toggling here would immediately close it again, so the button only opens.
  const handleDisclosureClick = () => {
    if (isMobile) {
      handleToggleSubmenu()
      return
    }

    handleOpenSubmenu()
  }

  if (!hasSubcategories) {
    return (
      <div className="category-mega-menu__category">
        <MegaRow
          href={href}
          label={title}
          iconSlug={iconSlug}
          onClick={handleRowClick}
        />
      </div>
    )
  }

  return (
    <div
      className="category-mega-menu__category"
      ref={hoverZoneRef}
      onMouseEnter={handleHoverZoneMouseEnter}
      onMouseLeave={handleHoverZoneMouseLeave}
      onFocus={handleSubmenuFocus}
      onBlur={handleSubmenuBlur}
    >
      <MegaRow
        href={href}
        label={title}
        labelId={labelId}
        iconSlug={iconSlug}
        onClick={handleRowClick}
      />
      <button
        type="button"
        className="category-mega-menu__disclosure"
        aria-labelledby={labelId}
        aria-expanded={isSubmenuOpen}
        aria-controls={flyoutId}
        onClick={handleDisclosureClick}
      >
        <Icon
          notSpaced
          color="dark"
          size={DISCLOSURE_CHEVRON_SIZE}
          svgComponent={ChevronRight}
          className="category-mega-menu__disclosure-chevron"
        />
      </button>
      <SubcategoryFlyout
        id={flyoutId}
        subcategories={subcategories}
        categoryTitle={title}
        isOpen={isSubmenuOpen}
        onClose={handleCloseSubmenu}
        onLinkClick={handleCategoryLinkClick}
        browseAllHref={browseAllHref}
        browseAllLabel={browseAllLabel}
      />
    </div>
  )
}

CategoryItem.propTypes = {
  title: string.isRequired,
  iconSlug: string,
  href: string,
  subcategories: array,
  browseAllHref: string,
  browseAllLabel: string,
}

export default memo(CategoryItem)
